import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ShoppingCart, Shield, Heart, Star, 
  Truck, Package, CheckCircle, Clock, AlertTriangle,
  Share2, MessageCircle, ChevronRight, Minus, Plus
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useWishlist } from '@/context/WishlistContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ProductRecommendations from '@/components/product/ProductRecommendations';
import RecentlyViewed from '@/components/product/RecentlyViewed';
import ProductImageGallery from '@/components/product/ProductImageGallery';
import VariantSelector from '@/components/product/VariantSelector';
import PreOrderModal from '@/components/product/PreOrderModal';
import { useRecentlyViewed } from '@/context/RecentlyViewedContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '971503847865';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const { isAuthenticated } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addRecentlyViewed } = useRecentlyViewed();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]); // All products in the same group (including current)
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [userRating, setUserRating] = useState(0);
  const [userReview, setUserReview] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [quantity, setQuantity] = useState(1);
  const [preOrderOpen, setPreOrderOpen] = useState(false);

  const isWishlisted = product ? isInWishlist(product.id) : false;
  const quantityInCart = product ? getItemQuantity(product.id) : 0;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        addRecentlyViewed(response.data);
        
        const ratingsRes = await axios.get(`${API}/products/${id}/ratings`);
        setRatings(ratingsRes.data.ratings || []);

        // Fetch sibling variants (if this product belongs to a group)
        try {
          const variantsRes = await axios.get(`${API}/products/${id}/variants`);
          setVariants(variantsRes.data || []);
        } catch (e) {
          setVariants([response.data]);
        }
      } catch (error) {
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate, addRecentlyViewed]);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    toast.success(`Added ${quantity} item(s) to cart`);
    setQuantity(1);
  };

  const handleBuyNow = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    toast.success('Added to cart');
    navigate('/cart');
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add to wishlist');
      navigate('/login');
      return;
    }
    
    const success = await toggleWishlist(product.id);
    if (success) {
      toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} on DXB2BOM!`,
          url: window.location.href
        });
      } catch (err) {
        // Share dialog cancelled by user — no action needed
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied!');
    }
  };

  const handleWhatsAppEnquiry = () => {
    const message = encodeURIComponent(`Hi! I'm interested in: ${product.name} (₹${product.price.toLocaleString()})`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const handleSubmitRating = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to submit a review');
      navigate('/login');
      return;
    }

    if (userRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmittingRating(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/products/${id}/rating`,
        { rating: userRating, comment: userReview },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Review submitted!');
      
      const ratingsRes = await axios.get(`${API}/products/${id}/ratings`);
      setRatings(ratingsRes.data.ratings || []);
      
      const productRes = await axios.get(`${API}/products/${id}`);
      setProduct(productRes.data);
      
      setUserRating(0);
      setUserReview('');
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingRating(false);
    }
  };

  const getStockStatus = () => {
    if (!product) return null;
    const stock = product.stock || 0;
    
    if (stock === 0) {
      return { label: 'Out of Stock', color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle };
    } else if (stock <= 3) {
      return { label: `Only ${stock} left!`, color: 'text-orange-600', bg: 'bg-orange-50', icon: Clock };
    } else if (stock <= 10) {
      return { label: `${stock} in stock`, color: 'text-amber-600', bg: 'bg-amber-50', icon: Package };
    } else {
      return { label: 'In Stock', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
    }
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setUserRating(star)}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
          >
            <Star
              className={`${size} ${
                star <= (interactive ? userRating : rating)
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-slate-200'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0047AB]"></div>
      </div>
    );
  }

  if (!product) return null;

  const stockStatus = getStockStatus();
  const avgRating = product.avg_rating || 0;
  const ratingCount = product.rating_count || 0;
  const discountPercent = product.original_price && product.original_price > product.price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;
  const isPreOrder = !!product.is_pre_order;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <button onClick={() => navigate('/')} className="hover:text-[#0047AB]">Home</button>
          <ChevronRight className="w-4 h-4" />
          <button onClick={() => navigate('/products')} className="hover:text-[#0047AB]">Products</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-900 truncate max-w-[200px]">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 pb-32 lg:pb-0">
          {/* Image Gallery — narrower column like Noon */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start"
          >
            <ProductImageGallery
              mainImage={product.image_url}
              images={product.images || []}
              productName={product.name}
              isWishlisted={isWishlisted}
              onWishlistToggle={handleWishlistToggle}
              stockStatus={stockStatus}
              warranty={product.warranty}
              avgRating={avgRating}
              ratingCount={ratingCount}
            />
          </motion.div>

          {/* Product Info — middle column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="lg:col-span-4 space-y-5"
          >
            {/* Brand & Category */}
            <div className="flex items-center gap-2">
              {product.brand && (
                <Link
                  to={`/products?brand=${encodeURIComponent(product.brand)}`}
                  className="text-xs font-semibold text-[#0047AB] bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                  data-testid="product-brand-link"
                >
                  {product.brand}
                </Link>
              )}
              <span className="text-xs text-slate-400 uppercase tracking-wide">
                {product.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-outfit font-bold text-slate-900 leading-tight">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                {renderStars(avgRating)}
                <span className="text-sm font-semibold text-slate-900">{avgRating.toFixed(1)}</span>
              </div>
              <span className="text-sm text-slate-500">({ratingCount} reviews)</span>
            </div>

            {/* Price */}
            {isPreOrder ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="product-pre-order-badge">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-sm font-semibold">
                  <Clock className="w-4 h-4" /> Pre-Order
                </span>
                <span className="text-sm text-slate-500">Price revealed when stock arrives</span>
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-3xl sm:text-4xl font-outfit font-bold text-slate-900" data-testid="product-price">
                    ₹{product.price.toLocaleString()}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <>
                      <span className="text-lg text-slate-400 line-through">
                        ₹{product.original_price.toLocaleString()}
                      </span>
                      <span className="text-sm bg-red-100 text-red-600 px-2 py-1 rounded font-semibold">
                        {discountPercent}% OFF
                      </span>
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-400">Inclusive of all taxes</p>
              </>
            )}

            {/* Stock Status */}
            <div className={`inline-flex items-center gap-2 ${stockStatus?.bg} ${stockStatus?.color} px-3 py-2 rounded-lg`}>
              {stockStatus && <stockStatus.icon className="w-4 h-4" />}
              <span className="text-sm font-medium">{stockStatus?.label}</span>
            </div>

            {/* Variant Selectors */}
            {variants && variants.length > 1 && (
              <VariantSelector currentProduct={product} variants={variants} />
            )}

            {/* Quantity Selector */}
            {!isPreOrder && product.stock > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700">Quantity:</span>
                <div className="flex items-center border border-slate-200 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-slate-50 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus className="w-4 h-4 text-slate-600" />
                  </button>
                  <span className="w-12 h-10 flex items-center justify-center font-semibold text-slate-900">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-slate-50 transition-colors"
                    disabled={quantity >= product.stock}
                  >
                    <Plus className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons removed — desktop uses right sidebar, mobile uses sticky bottom bar */}

            {/* Quick Actions */}
            <div className="flex gap-4 pt-2">
              <button
                onClick={handleWishlistToggle}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-red-500 transition-colors"
              >
                <Heart className={`w-4 h-4 ${isWishlisted ? 'text-red-500 fill-red-500' : ''}`} />
                {isWishlisted ? 'Saved' : 'Save'}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0047AB] transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <button
                onClick={handleWhatsAppEnquiry}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-green-600 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Ask on WhatsApp
              </button>
            </div>
          </motion.div>

          {/* Buy Sidebar - desktop only (Noon-style right card) */}
          <motion.aside
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="hidden lg:block lg:col-span-3 lg:sticky lg:top-24 lg:self-start"
            data-testid="buy-sidebar"
          >
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4">
              {/* Trust Strip */}
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                    <Truck className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Cash on Delivery</p>
                    <p className="text-xs text-slate-500">Pay when you receive</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">High Rated Seller</p>
                    <p className="text-xs text-slate-500">Trusted by 1000s</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                    <Shield className="w-4 h-4 text-[#0047AB]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Secure Transaction</p>
                    <p className="text-xs text-slate-500">100% Authentic</p>
                  </div>
                </div>
                {product.warranty && product.warranty !== 'Not Applicable' && product.warranty !== 'No Warranty' && (
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                      <Shield className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{product.warranty}</p>
                      <p className="text-xs text-slate-500">Manufacturer warranty</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-2 border-t border-slate-100 space-y-2">
                {isPreOrder ? (
                  <Button
                    size="lg"
                    className="w-full h-12 font-semibold bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => setPreOrderOpen(true)}
                    data-testid="sidebar-pre-order-btn"
                  >
                    <Clock className="w-5 h-5 mr-2" />
                    Pre-Order Now
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      className="w-full h-12 font-semibold bg-[#0047AB] hover:bg-[#003380] text-white"
                      onClick={handleAddToCart}
                      disabled={product.stock === 0}
                      data-testid="sidebar-add-to-cart-btn"
                    >
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Add to Cart
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full h-12 font-semibold border-2 border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB] hover:text-white"
                      onClick={handleBuyNow}
                      disabled={product.stock === 0}
                      data-testid="sidebar-buy-now-btn"
                    >
                      Buy Now
                    </Button>
                  </>
                )}
              </div>

              <div className="text-xs text-slate-500 flex items-center gap-1.5 pt-1">
                <Truck className="w-3.5 h-3.5" />
                {product.shipping_fee > 0 ? `₹${product.shipping_fee.toLocaleString()} Shipping` : 'Free Shipping'} • Dubai → India
              </div>
            </div>
          </motion.aside>
        </div>

        {/* Mobile Sticky Bottom Bar (Noon-style) - sits above the global BottomNavBar on phones */}
        <div className="lg:hidden fixed left-0 right-0 bottom-16 md:bottom-0 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] z-40" data-testid="mobile-buy-bar">
          <div className="flex items-stretch">
            <div className="flex flex-col items-center justify-center px-4 py-2 border-r border-slate-200 min-w-[72px]">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">Qty</span>
              <span className="text-lg font-semibold text-slate-900">{quantity}</span>
            </div>
            <button
              onClick={isPreOrder ? () => setPreOrderOpen(true) : handleAddToCart}
              disabled={!isPreOrder && product.stock === 0}
              className={`flex-1 disabled:bg-slate-300 text-white font-semibold text-base flex items-center justify-center gap-2 transition-colors ${
                isPreOrder ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#0047AB] hover:bg-[#003380]'
              }`}
              data-testid={isPreOrder ? 'mobile-pre-order-btn' : 'mobile-add-to-cart-btn'}
            >
              {isPreOrder ? (
                <>
                  <Clock className="w-5 h-5" />
                  PRE-ORDER
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  ADD TO CART
                </>
              )}
            </button>
          </div>
        </div>

        {/* Warranty Badge (above Description / Reviews) */}
        {product.warranty && product.warranty !== 'Not Applicable' && product.warranty !== 'No Warranty' && (
          <div className="mt-12 flex justify-center sm:justify-start">
            <span
              className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-100 px-4 py-2 rounded-full text-sm font-semibold"
              data-testid="badge-warranty"
            >
              <Shield className="w-4 h-4" />
              {product.warranty}
            </span>
          </div>
        )}

        {/* Tabs Section */}
        <div className="mt-6 border-t border-slate-100 pt-8">
          <div className="flex gap-8 border-b border-slate-200">
            {['description', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-sm font-semibold capitalize transition-colors relative ${
                  activeTab === tab ? 'text-[#0047AB]' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
                {tab === 'reviews' && ` (${ratingCount})`}
                {activeTab === tab && (
                  <motion.div
                    layoutId="tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0047AB]"
                  />
                )}
              </button>
            ))}
          </div>

          <div className="py-8">
            {activeTab === 'description' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-50 rounded-xl p-6"
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Product Description</h3>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                  {product.description || 'No description available for this product.'}
                </p>
              </motion.div>
            )}

            {activeTab === 'reviews' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Write Review */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Write a Review</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Your Rating</label>
                      {renderStars(userRating, true, 'w-7 h-7')}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Your Review</label>
                      <Textarea
                        value={userReview}
                        onChange={(e) => setUserReview(e.target.value)}
                        placeholder="Share your experience..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                    <Button
                      onClick={handleSubmitRating}
                      disabled={submittingRating || userRating === 0}
                      className="bg-[#0047AB] hover:bg-[#003380]"
                      data-testid="submit-review-btn"
                    >
                      {submittingRating ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </div>

                {/* Reviews List */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Customer Reviews</h3>
                  {ratings.length === 0 ? (
                    <div className="text-center py-8">
                      <Star className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                      <p className="text-slate-500">No reviews yet. Be the first!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {ratings.map((rating, index) => (
                        <div key={rating.id || index} className="border-b border-slate-200 pb-4 last:border-0">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#0047AB] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                              {rating.user_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-medium text-slate-900 text-sm">{rating.user_name || 'Anonymous'}</p>
                                <span className="text-xs text-slate-400">
                                  {new Date(rating.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                </span>
                              </div>
                              <div className="mb-2">{renderStars(rating.rating, false, 'w-3.5 h-3.5')}</div>
                              {rating.comment && (
                                <p className="text-slate-600 text-sm">{rating.comment}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <ProductRecommendations 
          currentProductId={id} 
          category={product.category}
          limit={4}
        />

        {/* Recently Viewed */}
        <RecentlyViewed excludeId={id} />
      </div>

      {/* Pre-Order Modal */}
      <PreOrderModal
        open={preOrderOpen}
        onOpenChange={setPreOrderOpen}
        product={product}
      />
    </div>
  );
};

export default ProductDetailPage;
